import { inject, Injectable } from '@angular/core';
import { Observable, combineLatest, map, switchMap, of } from 'rxjs';
import { ActiveCintraProductService } from '@app/active-cintra-product.service';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { ActiveFeatureService } from '@app/active-feature.service';
import { LoggingService } from '@logging/logging.service';
import { SidebarMenuConfig } from '../../../component-library/layout';
import { MenuItem } from '../../../component-library/layout/menu-item/menu-interfaces';
import { CintraPayNavigationService } from './nav-links/cintra-pay-navigation.service';
import { BureauNavigationService } from './nav-links/bureau-navigation.service';
import { AdminHubNavigationService } from './nav-links/admin-hub-navigation.service';
import { EmployeeHubNavigationService } from './nav-links/employee-hub-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class SidebarMenuService {
  private logger = inject(LoggingService);
  constructor(
    private activeCintraProductService: ActiveCintraProductService,
    private cintraPayNavigationService: CintraPayNavigationService,
    private bureauNavigationService: BureauNavigationService,
    private adminHubNavigationService: AdminHubNavigationService,
    private employeeHubNavigationService: EmployeeHubNavigationService,
    private activeSecurity: ActiveSecurityContextStateService,
    private activeFeatureService: ActiveFeatureService
  ) {}

  getSidebarMenuConfig$(): Observable<SidebarMenuConfig> {
    return combineLatest([
      this.activeCintraProductService.activeProduct$,
      this.activeSecurity.activeMembership$,
      this.activeFeatureService.activeFeature$,
      this.activeFeatureService.activeSection$
    ]).pipe(
      switchMap(([activeProduct, membership, activeFeature, activeSection]) => {
        if (!membership) {
          return of({ sections: [] });
        }

        let config$: Observable<SidebarMenuConfig>;

        switch (activeProduct) {
          case 'Payroll':
            config$ = this.cintraPayNavigationService.getSidebarMenuConfig$();
            break;
          case 'Bureau':
            config$ = this.bureauNavigationService.getSidebarMenuConfig$();
            break;
          case 'EmployeeHub':
            config$ = this.employeeHubNavigationService.getSidebarMenuConfig$();
            break;
          case 'AdminHub':
            config$ = this.adminHubNavigationService.getSidebarMenuConfig$();
            break;
          default:
            config$ = of({ sections: [] });
        }

        return config$.pipe(
          map((config) => {
            // Compute active states
            this.computeActiveStates(config, activeFeature, activeSection);

            return config;
          })
        );
      })
    );
  }

  private computeActiveStates(config: SidebarMenuConfig, activeFeature: string, activeSection: string | undefined) {
    config.sections.forEach((section) => {
      section.items.forEach((item) => {
        // Only compute if not explicitly set
        if (item.active === undefined) {
          item.active = this.isItemActive(item, activeFeature, activeSection);
        }

        item.submenu?.items.forEach((child) => {
          if (child.active === undefined) {
            child.active = this.isItemActive(child, activeFeature, activeSection);
          }
        });
      });
    });

    // Also compute active states for bottom nav items
    if (config.bottomNavItems) {
      config.bottomNavItems.forEach((item) => {
        // Only compute if not explicitly set
        if (item.active === undefined) {
          item.active = this.isItemActive(item, activeFeature, activeSection);
        }
      });
    }
  }

  private isItemActive(item: MenuItem, activeFeature: string, activeSection: string | undefined): boolean {
    const itemFeature = (item as any).feature;
    const itemSection = (item as any).section;

    // If this item has a section, only highlight if the section matches
    if (itemSection) {
      return itemSection === activeSection;
    }

    // If this is a top-level feature item (no section), only highlight if:
    // 1. The feature matches AND
    // 2. There's no active section (meaning we're at the top level of this feature)
    if (itemFeature) {
      return itemFeature === activeFeature && !activeSection;
    }

    return false;
  }
}
